import { useMemo, useState } from 'react';
import { PrimaryButton, SecondaryButton, Select, StatusBar } from '@weareopenr/component-library';
import { IPublishFormValues, PublishType, Steps } from '../../types';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { getPosInstances } from 'src/helpers/channels/getPosInstances';
import styles from './SelectInstance.module.scss';

const NO_INSTANCES_MESSAGE = 'No point of sale instances are set up for this brand.';

export const SelectInstance = ({
  submitDataToParent,
  defaultValues,
}: {
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: Steps) => void;
  defaultValues: Pick<IPublishFormValues, 'type' | 'instanceId'>;
}) => {
  const { data: externalSystems, isLoading } = useExternalSystems();
  const [chosenInstanceId, setChosenInstanceId] = useState(defaultValues.instanceId ?? undefined);

  const posInstances = useMemo(() => getPosInstances(externalSystems), [externalSystems]);

  const instanceOptions = posInstances.map(({ extensionInstanceId, extensionInstanceName }) => ({
    value: extensionInstanceId,
    name: extensionInstanceName,
  }));

  // With a single instance there is nothing to choose, so preselect it rather than
  // blocking Continue behind a redundant click.
  const selectedInstanceId =
    chosenInstanceId ?? (posInstances.length === 1 ? posInstances[0].extensionInstanceId : undefined);

  const hasNoInstances = !isLoading && posInstances.length === 0;

  const handleContinue = () => {
    submitDataToParent(
      { instanceId: selectedInstanceId },
      defaultValues.type === PublishType.PRODUCT ? Steps.excludeProducts : Steps.venues,
    );
  };

  return (
    <>
      <ModalTitle
        title="Point of Sale System"
        subTitle="Choose which point of sale instance will receive this publish."
      />
      <div className={styles['select-container']}>
        {hasNoInstances ? (
          <StatusBar variant="info" message={NO_INSTANCES_MESSAGE} />
        ) : (
          <Select
            name="instanceId"
            label="Instance"
            options={instanceOptions}
            value={selectedInstanceId}
            onChange={setChosenInstanceId}
            loading={isLoading}
            disabled={isLoading}
          />
        )}
      </div>
      <ButtonContainer
        left={
          <SecondaryButton
            onClick={() => submitDataToParent({}, Steps.publishDetails)}
            size="full-width"
            buttonStyle="outline"
          >
            Back
          </SecondaryButton>
        }
        right={
          <PrimaryButton onClick={handleContinue} size="full-width" disabled={!selectedInstanceId}>
            Continue
          </PrimaryButton>
        }
      />
    </>
  );
};
